import { Chip } from '@material-ui/core';
import cx from 'classnames';
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getBaseUrl } from '@core/base-url';
import { AppId, useAppUrl, useConfig, useConfigAll } from '@core/contexts/ConfigContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Tooltip } from '@shared/components/tooltip';
import { AtlasLogoShortIcon } from '@shared/icons/atlas-logo-short';
import { AwardIcon } from '@shared/icons/award';
import { BarChartIcon } from '@shared/icons/bar-chart-new';
import { BuildingIcon } from '@shared/icons/building-new';
import { CalendarCheckIcon } from '@shared/icons/calendar-check-new';
import { ChartBreakoutSquareIcon } from '@shared/icons/chart-breakout-square';
import { CheckCircleBrokenIcon } from '@shared/icons/check-circle-broken-new';
import { ChevronLeftIcon } from '@shared/icons/chevron-left';
import { ChevronRightIcon } from '@shared/icons/chevron-right';
import { ClipboardCheckIcon } from '@shared/icons/clipboard-check-new';
import { ClockRewindIcon } from '@shared/icons/clock-rewind';
import { CodeSquareIcon } from '@shared/icons/code-square';
import { CoinsHandIcon } from '@shared/icons/coins-hand';
import { FileCheckIcon } from '@shared/icons/file-check';
import { FileCheck2Icon } from '@shared/icons/file-check-2';
import { Globe2Icon } from '@shared/icons/globe-2';
import { HelpIcon } from '@shared/icons/help';
import { HomeIcon } from '@shared/icons/home';
import { KatoLogoShortInverseIcon } from '@shared/icons/kato-logo-short-inverse';
import { MailIcon } from '@shared/icons/mail';
import { PinIcon } from '@shared/icons/pin-new';
import { PropertyIcon } from '@shared/icons/property';
import { SettingsIcon } from '@shared/icons/settings';
import { UserCheckIcon } from '@shared/icons/user';
import { UserPlusIcon } from '@shared/icons/user-plus';
import { WarehouseIcon } from '@shared/icons/warehouse';
import {
  AppGroupedNavItem,
  AppGroupedNavSubItem,
  AppNavAllItemKeys,
  AppNavGroupItemKeys,
  AppNavItem,
} from '@shared/models/config';
import { AtlasEnabled } from '@shared/models/IUser';
import { NewButtonColour } from '@shared/types/common/button';
import { getQueriesAsSearch, reduceStringRecordToObject } from '@shared/utils/common';
import { getDefaultPath, getTransformedRoute } from '@shared/utils/route';

import { SideNavigationExploreUpsellItem } from './components/explore-upsell-item';
import { AgentSocietyNavigationLogo } from './components/logo/agent-society-navigation-logo';
import { AtlasNavigationLogo } from './components/logo/atlas-navigation-logo/AtlasNavigationLogo';
import { ComptonNavigationLogo } from './components/logo/compton-navigation-logo';
import { JllNavigationLogo } from './components/logo/jll-navigation-logo';
import { KatoNavigationLogo } from './components/logo/kato-navigation-logo';
import { RxNavigationLogo } from './components/logo/rx-navigation-logo';
import { SideNavigationGroup, SideNavigationGroupItem } from './components/side-navigation-group';
import { SideNavigationIntercomAction } from './components/side-navigation-intercom-action';
import { SideNavigationItem } from './components/side-navigation-item';
import { SideNavigationUserItem } from './components/side-navigation-user-item';
import { SideNavigationWorkspaceItem } from './components/side-navigation-workspace-item/SideNavigationWorkspaceItem';
import { styles } from './SideNavigation.styles';
import { BadgeTheme } from '../badge-new';
import { Button } from '../button-new';

export type MenuOpenHash = { [key in AppNavGroupItemKeys]: boolean };
export type IconHash = { [key in AppNavAllItemKeys]: ReactNode };

interface GroupMenus {
  [key: string]: string[];
}

const iconHash: IconHash = {
  organisation_advisory: <FileCheckIcon />,
  organisation_agents: <UserCheckIcon />,
  organisation_campaigns: <MailIcon />,
  organisation_ias: <WarehouseIcon />,
  organisation_crm: <UserCheckIcon />,
  organisation_disposals: <BuildingIcon />,
  organisation_home: <HomeIcon />,
  organisation_insights: <BarChartIcon />,
  organisation_integrations: <CodeSquareIcon />,
  organisation_leases: <FileCheck2Icon />,
  organisation_properties: <PropertyIcon />,
  organisation_search: <Globe2Icon />,
  organisation_search_availability: <Globe2Icon />,
  organisation_requirements: <ClipboardCheckIcon />,
  organisation_sales: <CoinsHandIcon />,
  organisation_tasks: <CheckCircleBrokenIcon />,
  organisation_viewings: <CalendarCheckIcon />,
  organisation_wip_fees: <PinIcon />,
  organisation_rankings: <AwardIcon />,
  organisation_manage: <ClockRewindIcon />,

  marketplace_dashboard: <i className="icon icon-society" />,
  marketplace_disposals: <BuildingIcon />,
  marketplace_events: <i className="icon icon-calendar" />,
  marketplace_insights: <BarChartIcon />,
  marketplace_join_society: <UserPlusIcon />,
  marketplace_members: <i className="icon icon-users" />,
  marketplace_preferences: <SettingsIcon />,
  marketplace_requirements: <ClipboardCheckIcon />,
};

export interface SideNavigationProps extends WithStyles<typeof styles> {
  collapsed: boolean;
  onCollapse: () => void;
  workspacesEnabled?: boolean;
}

const initialMenuState: Record<AppNavGroupItemKeys, boolean> = {
  organisation_advisory: false,
  organisation_crm: false,
  organisation_disposals: false,
  organisation_insights: false,
  organisation_leases: false,
  organisation_sales: false,
  organisation_wip_fees: false,

  marketplace_insights: false,
};

export interface SideNavigationProps extends WithStyles<typeof styles> {
  collapsed: boolean;
  onCollapse: () => void;
}

const SideNavigationComponent: FC<SideNavigationProps> = ({ classes, collapsed, workspacesEnabled, onCollapse }) => {
  const { appUrl } = useAppUrl();
  const { appId, constants } = useConfigAll();
  const { appLogos, appNav, appUrl: agencyAppUrl, atlasAppUrl, coogan, marketingUrl, user } = useConfig();
  const { pathname } = useLocation();

  const [localCollapse, setLocalCollapse] = useState(collapsed);
  const [menu, setMenu] = useState<MenuOpenHash>(initialMenuState);

  const groupMenus: GroupMenus = useMemo(() => {
    return [...appNav.organisation_items_order, ...appNav.marketplace_items_order].reduce((result, itemKey) => {
      const navItem = appNav[itemKey] as AppGroupedNavItem;

      if (navItem && Array.isArray(navItem?.items_order) && navItem?.items_order?.length) {
        result[itemKey] = navItem.items_order.reduce((subResult, subItemKey) => {
          const subItem = navItem.items[subItemKey] as AppGroupedNavSubItem;

          if (subItem && subItem.url) {
            subResult.push(getTransformedRoute(appUrl, subItem.url));
          }

          return subResult;
        }, [] as string[]);
      }

      return result;
    }, {} as GroupMenus);
  }, [appNav, appUrl]);

  useEffect(() => {
    const _menuState: MenuOpenHash = reduceStringRecordToObject(groupMenus, (result, _, key) => {
      result[key as AppNavGroupItemKeys] = groupMenus[key]?.includes(pathname) ?? false;
      return result;
    });
    setMenu(_menuState);
  }, [groupMenus, pathname]);

  const renderItems = useCallback(
    (itemKeys: AppNavAllItemKeys[]): ReactNode => {
      return itemKeys.reduce((result, itemKey) => {
        const navItem = appNav[itemKey];

        // Check if visible
        if (!navItem || !navItem.visible) {
          return result;
        }

        if ('items_order' in navItem) {
          const groupedNavItem = navItem as AppGroupedNavItem;
          const menuKey = itemKey as AppNavGroupItemKeys;

          const subItems = groupedNavItem.items_order.reduce((subResult, subItemKey) => {
            if (!(subItemKey in groupedNavItem.items)) {
              return subResult;
            }

            const subItem = groupedNavItem.items[subItemKey] as AppGroupedNavSubItem;

            if (subItem.url && subItem.visible) {
              // Prioritise the short label since it'll be shown in grouped item
              subResult.push({
                title: subItem.label_short || subItem.label,
                to: subItem.url,
              });
            }

            return subResult;
          }, [] as SideNavigationGroupItem[]);

          // Only add group item if we have items
          if (subItems.length) {
            result.push(
              <SideNavigationGroup
                classes={{
                  link: classes.sideNavigationItemGroupLink,
                  linkActive: classes.sideNavigationItemLinkActive,
                  linkDot: classes.sideNavigationItemGroupLinkDot,
                  linkDotContainer: classes.sideNavigationItemGroupLinkDotContainer,
                  linkText: classes.sideNavigationItemTitle,
                  toggleButton: classes.sideNavigationItemGroupToggleButton,
                  toggleIcon: classes.sideNavigationItemGroupToggleIcon,
                  toggleStartIcon: classes.sideNavigationItemGroupStartIcon,
                  toggleText: classes.sideNavigationItemTitle,
                }}
                key={itemKey}
                expanded={menu[menuKey]}
                icon={iconHash[itemKey]}
                items={subItems}
                title={groupedNavItem.label}
                setExpanded={() => setMenu((_prev) => ({ ...initialMenuState, [itemKey]: !_prev[menuKey] }))}
                badge={
                  groupedNavItem.badge_label
                    ? { theme: BadgeTheme.app, content: groupedNavItem.badge_label }
                    : undefined
                }
              />
            );
          }
        } else {
          const singleNavItem = navItem as AppNavItem;

          // Add non-grouped item
          if (singleNavItem.url) {
            result.push(
              <SideNavigationItem
                classes={{
                  link: classes.sideNavigationItemLink,
                  linkActive: classes.sideNavigationItemLinkActive,
                  title: classes.sideNavigationItemTitle,
                }}
                key={itemKey}
                startIcon={iconHash[itemKey]}
                to={singleNavItem.url}
                title={singleNavItem.label}
                badge={singleNavItem.badge_label ? { theme: BadgeTheme.app, content: singleNavItem.badge_label } : undefined}
              />
            );
          }
        }

        return result;
      }, [] as ReactNode[]);
    },
    [appNav, appUrl, classes, iconHash, menu]
  );

  const renderFixedGroupItems = useCallback(
    (itemKeys: AppNavAllItemKeys[], icon: ReactNode, title: string): ReactNode => {
      const items = itemKeys.reduce((result, itemKey) => {
        const navItem = appNav[itemKey];

        // Check if visible
        if (!navItem || !navItem.visible) {
          return result;
        }

        if ('items_order' in navItem) {
          const groupedNavItem = navItem as AppGroupedNavItem;

          groupedNavItem.items_order.forEach((subItemKey) => {
            const subItem = groupedNavItem.items[subItemKey] as AppGroupedNavSubItem;

            if (subItem.visible && subItem.url) {
              result.push({ title: subItem.label, to: subItem.url });
            }
          });
        } else {
          const singleNavItem = navItem as AppNavItem;

          // Add non-grouped item
          if (singleNavItem.url && singleNavItem.visible) {
            result.push({
              to: singleNavItem.url,
              title: singleNavItem.label,
              badge: singleNavItem.badge_label ? { theme: BadgeTheme.app, content: singleNavItem.badge_label } : undefined,
            });
          }
        }

        return result;
      }, [] as SideNavigationGroupItem[]);

      return (
        <SideNavigationGroup
          classes={{
            link: classes.sideNavigationItemGroupLink,
            linkActive: classes.sideNavigationItemLinkActive,
            linkDot: classes.sideNavigationItemGroupLinkDot,
            linkDotContainer: classes.sideNavigationItemGroupLinkDotContainer,
            linkText: classes.sideNavigationItemTitle,
            toggleButton: classes.sideNavigationItemGroupToggleButton,
            toggleIcon: classes.sideNavigationItemGroupToggleIcon,
            toggleStartIcon: classes.sideNavigationItemGroupStartIcon,
            toggleText: classes.sideNavigationItemTitle,
          }}
          key={'itemKey'}
          fixed
          icon={icon}
          items={items}
          title={title}
        />
      );
    },
    [appNav, appUrl]
  );

  useEffect(() => {
    setLocalCollapse(collapsed);
  }, [collapsed]);

  const inviteAction = useMemo(() => {
    if (appId === AppId.Landlord) {
      return (
        <div className={classes.sideNavigationInvite}>
          <Button
            classes={{
              root: classes.inviteAction,
              buttonLabel: classes.inviteActionLabel,
            }}
            colour={NewButtonColour.PrimaryTeal}
            text={t('invite_teammates')}
            startIcon={<UserPlusIcon classes={{ root: classes.inviteIcon }} />}
            onClick={() =>
              (window.location.href = `${appNav.settings_organisation.url}/users${getQueriesAsSearch({
                create: true,
              })}`)
            }
          />
        </div>
      );
    }
  }, [appId, classes]);

  const logo = useMemo(() => {
    const defaultPath = getDefaultPath(appUrl, appNav);

    // Only show the kato logo for landlord
    if (appId === AppId.Landlord) {
      return <KatoNavigationLogo classes={{ logo: classes.katoLogo }} url={defaultPath} />;
    }

    // Only show the logo for atlas
    if (appId === AppId.Atlas) {
      return <AtlasNavigationLogo classes={{ logo: classes.katoLogo }} url={defaultPath} />;
    }

    switch (appLogos.large_id) {
      case 'society-large': {
        return <AgentSocietyNavigationLogo katoUrl={marketingUrl} url={defaultPath} />;
      }

      case 'jll-large': {
        return <JllNavigationLogo appLogos={appLogos} katoUrl={marketingUrl} url={defaultPath} />;
      }

      case 'compton-large': {
        return <ComptonNavigationLogo appLogos={appLogos} katoUrl={marketingUrl} url={defaultPath} />;
      }

      case 'rx-large': {
        return <RxNavigationLogo appLogos={appLogos} katoUrl={marketingUrl} url={defaultPath} />;
      }

      case 'insight-large':
      default: {
        return <KatoNavigationLogo url={defaultPath} />;
      }
    }
  }, [appId, appLogos, marketingUrl]);

  const workspacesItems = useMemo(() => {
    const items = [
      {
        icon: <KatoLogoShortInverseIcon />,
        active: appId === AppId.Agency,
        link: agencyAppUrl,
      },
    ];

    // Only show Atlas workspace item if atlas_enabled is 1 or 2
    const atlasEnabled = user?.organisation?.atlas_enabled ?? AtlasEnabled.Disabled;
    if (atlasEnabled !== AtlasEnabled.Disabled) {
      items.push({
        icon: <AtlasLogoShortIcon />,
        active: appId === AppId.Atlas,
        link: atlasAppUrl,
      });
    }

    return items;
  }, [appId, agencyAppUrl, atlasAppUrl, user.organisation?.atlas_enabled]);

  return (
    <div className={classes.root}>
      {workspacesEnabled && (
        <div className={classes.workspaces}>
          {workspacesItems.map((el) => (
            <SideNavigationWorkspaceItem {...el} key={el.link} />
          ))}
        </div>
      )}
      <div
        className={cx(classes.navigation, {
          [classes.navigationCollapsed]: localCollapse,
          [classes.landlordNavigation]: appId === AppId.Landlord,
        })}
        onMouseEnter={() => {
          if (collapsed) {
            setLocalCollapse(false);
          }
        }}
        onMouseLeave={() => {
          if (collapsed) {
            setLocalCollapse(true);
          }
        }}
      >
        <Chip
          clickable
          onClick={onCollapse}
          classes={{
            root: cx(classes.collapseBtn, { [classes.collapseBtnVisible]: collapsed }),
          }}
          icon={collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        />
        {coogan && (
          <Tooltip
            title={`Impersonating: ${user.name} (${user.organisation?.name})`}
            arrow={true}
            placement="right"
            classes={{
              root: classes.impersonateContainer,
              tooltip: classes.impersonateTooltip,
              arrow: classes.impersonateTooltipArrow,
            }}
          >
            <a
              href={`${getBaseUrl()}impersonate/leave`}
              className={cx(classes.impersonateLink, {
                [classes.impersonateLinkProduction]: constants.APP_ENV === 'production',
              })}
            />
          </Tooltip>
        )}
        <div className={cx(classes.header, { [classes.hiddenContent]: localCollapse })}>{logo}</div>
        <div className={cx(classes.contentContainer, { [classes.hiddenContent]: localCollapse })}>
          <div className={classes.content}>
            {appNav.organisation && renderItems(appNav.organisation_items_order)}
            {appNav.upgrade_cta && <SideNavigationExploreUpsellItem classes={{ root: classes.upgradeCta }} />}
            {appNav.marketplace && <div className={classes.marketplaceSectionContainer} />}
            {appNav.marketplace && !appNav.marketplace_fixed_group && (
              <div className={classes.marketplaceSectionLabel}>{t('marketplace')}</div>
            )}
            {appNav.marketplace && !appNav.marketplace_fixed_group && renderItems(appNav.marketplace_items_order)}
            {appNav.marketplace &&
              appNav.marketplace_fixed_group &&
              renderFixedGroupItems(
                appNav.marketplace_items_order,
                <i className="icon icon-society" />,
                t('marketplace')
              )}
          </div>
        </div>
        {inviteAction}
        <div className={cx(classes.footer, { [classes.hiddenContent]: localCollapse })}>
          <div className={classes.footerDivider} />
          <SideNavigationIntercomAction
            classes={{ root: classes.sideNavigationItemLink, actionLabel: classes.sideNavigationItemTitle }}
            label={t('i_need_help')}
            startIcon={<HelpIcon />}
          />
          {appNav.schedule_invites.visible && (
            <SideNavigationItem
              classes={{
                title: classes.sideNavigationItemTitle,
                link: classes.sideNavigationItemLink,
                linkActive: classes.sideNavigationItemLinkActive,
              }}
              startIcon={<ChartBreakoutSquareIcon />}
              title={t('schedule_invites')}
              to={appNav.schedule_invites.url}
              count={appNav.counts?.schedule_invites}
            />
          )}
          <SideNavigationUserItem
            appNav={appNav}
            classes={{ root: classes.sideNavigationItemLink, name: classes.sideNavigationItemTitle }}
            user={user}
          />
        </div>
        {!collapsed && <div className={classes.collapseLine} onClick={onCollapse} />}
      </div>
    </div>
  );
};

export const SideNavigation = withStyles(styles)(SideNavigationComponent);
